import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Modal as BaseModal, Width, Button, SubmitStates } from '@figure/frontlib-gui';
import * as Yup from 'yup';
import { withFormik, Form } from 'formik';
import { SingleInputSubmit, Color } from '@figure/frontlib-gui/lib/Dashboard';
import { useProfile } from 'shared/hooks';

const Modal = styled(BaseModal)`
  padding: 24px;
  text-align: center;

  @media (min-width: ${Width.SM}px) {
    padding: 75px;
  }
`;

const Disclaimer = styled.span`
  display: block;
  color: ${Color.GREY_7};
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
`;

const FormContainer = styled.div`
  max-width: 300px;
  margin: auto;
  text-align: left;
`;

const ShareReferralModal = ({
  values,
  touched,
  errors,
  setFieldValue,
  handleSubmit,
  isOpen,
  handleClose,
  submitState,
  setSubmitState,
  showReferralConfirm,
  setShowReferralConfirm,
}) => {
  const { profile } = useProfile(false);
  const textMessage = encodeURI(
    `I just got a loan with Figure! Get a $150 gift card when you complete your Personal Loan at https://www.figure.com/ and use my email ${profile.email}`
  );
  const hrefSms = `sms:?&body=${textMessage}`;
  return (
    <Modal size="md" isOpen={isOpen} onClose={handleClose}>
      {showReferralConfirm ? (
        <>
          <h1>Thank you for submitting your application.</h1>
          <p>
            Your $150 gift card will be sent via email when your application is approved and your
            loan funds.
          </p>
          <p>
            Want to earn more? Click below to see how to share, and keep earning $150 each time you
            refer a friend.
          </p>
          <Button onClick={() => setShowReferralConfirm(false)}>Earn More</Button>
        </>
      ) : (
        <>
          <h1>Share with a friend and receive a $150 gift card.</h1>
          <p>
            Earn $150 when you refer someone for a Personal Loan. Share via email below or{' '}
            <a href={hrefSms}>send them a text message</a>.
          </p>

          <Form onSubmit={handleSubmit}>
            <FormContainer>
              <SingleInputSubmit
                id="referral-email"
                label=""
                type="email"
                placeholder="email address"
                value={values.email}
                handleSubmit={handleSubmit}
                errorText={touched.email && errors.email}
                submitState={submitState}
                onChange={(e) => {
                  setSubmitState(SubmitStates.DEFAULT);
                  setFieldValue('email', e.target.value);
                }}
                required
              />
              <Disclaimer>
                For either party to be eligible to receive the $150 gift card, the referred personal
                loan must be funded.
              </Disclaimer>
            </FormContainer>
          </Form>
        </>
      )}
    </Modal>
  );
};

ShareReferralModal.propTypes = {
  submitState: PropTypes.string.isRequired,
  setSubmitState: PropTypes.func.isRequired,
  isOpen: PropTypes.bool.isRequired,
  showReferralConfirm: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  handleSubmit: PropTypes.func.isRequired,
  values: PropTypes.objectOf(PropTypes.string).isRequired,
  errors: PropTypes.objectOf(PropTypes.string).isRequired,
  touched: PropTypes.objectOf(PropTypes.bool).isRequired,
  setFieldValue: PropTypes.func.isRequired,
  setShowReferralConfirm: PropTypes.func.isRequired,
};

export default withFormik({
  mapPropsToValues: () => ({
    email: '',
  }),
  validationSchema: () =>
    Yup.object().shape({
      email: Yup.string().required().email('Invalid'),
    }),
  handleSubmit: (data, { props, resetForm, setFieldError }) => {
    if (props.refereeEmail.toLowerCase() === data.email.toLowerCase()) {
      setFieldError('email', 'Self referral is not allowed');
    } else {
      resetForm();
      props.handleSubmit(data);
    }
  },
})(ShareReferralModal);
